import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { PropertyDropdown } from '../../../components/Dashboard/PropertyDropdown';
import { ZeosApiContext } from '../../../context/ZeosApiContext';
import { useIncentivesByProperty } from '../../../hooks/useIncentivesByProperty';
import { getSearchParamNumber } from '../../../services/nav/QueryStringHelpers';
import { AccountAuth } from '../../../services/auth/AccountAuth';
import { ICompanyClaims } from '../../../services/zeos-api/contracts/Account';
import { IIncentiveContract } from '../../../services/zeos-api/contracts/Incentive';
import { IScreeningIncentiveMatchRequestResponse } from '../../../services/zeos-api/contracts/Screening';
import { DashboardFooter } from '../layout/DashboardFooter';
import { DashboardHeader } from '../layout/DashboardHeader';
import { IncentivesResources } from './IncentivesResources';
import { IncentivesTable } from './IncentivesTable';

export const Incentives = () => {
  const api = useContext(ZeosApiContext);
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState<{
    key: number;
    value: string;
  } | null>(null);
  const [activeCompany, setActiveCompany] = useState<ICompanyClaims>();
  const [selectedPropertyId, setSelectedPropertyId] = useState<number | null>(
    null
  );
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [screeningMatch, setScreeningMatch] =
    useState<IScreeningIncentiveMatchRequestResponse | null>(null);
  const [screeningIncentives, setScreeningIncentives] = useState<IIncentiveContract[]>([]);
  const [screeningLoading, setScreeningLoading] = useState(false);
  const [screeningError, setScreeningError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const urlPropertyId = getSearchParamNumber("property", searchParams);
  const screeningRunGuid = searchParams.get("screeningRun");
  const shouldUpdateSummary = searchParams.has("updateSummary");
  const isScreeningDerived = Boolean(screeningRunGuid);
  const { rawIncentives, loading, error, refresh } = useIncentivesByProperty(
    isScreeningDerived ? null : selectedPropertyId,
    shouldUpdateSummary
  );
  const displayIncentives = isScreeningDerived ? screeningIncentives : rawIncentives;
  const displayLoading = isScreeningDerived ? screeningLoading : loading;
  const displayError = isScreeningDerived ? screeningError : error;

  // 4037: dataGaps carries raw matcher reason tags (e.g.
  // "NeedsVerification:EligibleApplicantClassesUnauthored"). They are internal
  // identifiers with no display mapping, so they are not rendered to customers.
  // The field is still returned by the API and remains available to the admin
  // diagnostics endpoint.

  const hasPendingAiAnalysis = useMemo(
    () =>
      displayIncentives.some(
        (item) => (item.aiAnalysisState || "").toLowerCase() === "pending"
      ),
    [displayIncentives]
  );

  const eligibilityLabel = isScreeningDerived
    ? "Preliminary / screening-derived matches"
    : "Verified eligibility";

  const eligibilityHint = isScreeningDerived
    ? "Results are ranked from screening inputs. Confirm property facts and program rules before quoting eligibility or value."
    : "Results passed property hard-filter matching. Ranked by benefit, timing, confidence, and program fit.";

  useEffect(() => {
    const initAsync = async () => {
      if (activeCompany && !isScreeningDerived) {
        if (urlPropertyId) {
          setSelectedPropertyId(urlPropertyId);
        } else if (activeCompany.properties.length > 0) {
          setSelectedPropertyId(activeCompany.properties[0].propertyId);
        }
      }
    };
    initAsync();
  }, [activeCompany, urlPropertyId, isScreeningDerived]);

  useEffect(() => {
    if (!screeningRunGuid) {
      setScreeningMatch(null);
      setScreeningIncentives([]);
      setScreeningError(null);
      return;
    }

    let isMounted = true;
    const loadScreeningIncentivesAsync = async () => {
      setScreeningLoading(true);
      setScreeningError(null);
      try {
        const [handoffResponse, eligibleResponse] = await Promise.all([
          api.Screening.getIncentiveMatchRequestByGuidAsync(screeningRunGuid),
          api.Screening.getEligibleIncentivesByRunGuidAsync(screeningRunGuid),
        ]);
        if (!isMounted) return;
        setScreeningMatch(handoffResponse.data);
        setScreeningIncentives(eligibleResponse.data || []);
      } catch (err: any) {
        if (!isMounted) return;
        console.error("Failed to load screening-derived incentives", err);
        setScreeningError("Could not load screening-derived incentives");
        setScreeningIncentives([]);
      } finally {
        if (isMounted) {
          setScreeningLoading(false);
        }
      }
    };

    loadScreeningIncentivesAsync();
    return () => {
      isMounted = false;
    };
  }, [api, screeningRunGuid]);

  useEffect(() => {
    const loadUserAsync = async () => {
      const user = await AccountAuth.zeosUserAsync();
      setIsSuperAdmin(Boolean(user?.isSuperAdmin));
    };
    loadUserAsync();
  }, []);

  const handlePropertySelect = (propertyId: number) => {
    setSelectedPropertyId(propertyId);
  };

  const handleBackToScreener = () => {
    const params = new URLSearchParams();
    if (screeningRunGuid) {
      params.set("run", screeningRunGuid);
    }

    const companyId = getSearchParamNumber("company", searchParams) || activeCompany?.companyId;
    if (companyId) {
      params.set("company", companyId.toString());
    }

    navigate(`/screening${params.toString() ? `?${params.toString()}` : ""}`);
  };

  const screeningBanner = useMemo(() => {
    if (!isScreeningDerived) return null;
    const address = screeningMatch?.property?.address || "Screening run";
    const confidence = screeningMatch?.screening?.confidence || "Pending";
    const lifecycle = [
      screeningMatch?.property?.buildingAge ? `${screeningMatch.property.buildingAge} years old` : "",
      screeningMatch?.property?.yearBuilt ? `built ${screeningMatch.property.yearBuilt}` : "",
      screeningMatch?.property?.lastRetrofitYear ? `last retrofit ${screeningMatch.property.lastRetrofitYear}` : "",
    ].filter(Boolean).join(" - ");
    return `${address} - Screening-derived property${lifecycle ? ` - ${lifecycle}` : ""} - ${confidence} confidence`;
  }, [isScreeningDerived, screeningMatch]);

  return (
    <div className="flex flex-col min-h-screen">
      <DashboardHeader companyCallback={setActiveCompany} />
      <main className="bg-zeos-white-md p-4 flex-grow">
        <div
          className="grid grid-cols-12 gap-6 font-sans"
          onClick={() => setIsDropdownOpen(false)}
        >
          <div className="col-span-12">
            {isScreeningDerived ? (
              <div className="flex flex-col gap-3 rounded-md border border-zeos-grey bg-white px-4 py-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <div className="text-base font-semibold text-zeos-black">
                    Screening-Derived Incentives
                  </div>
                  <div className="mt-1 text-sm text-slate-700">
                    {screeningBanner || "Matched from the screening run."}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleBackToScreener}
                  className="self-start rounded-md border border-zeos-grey bg-white px-3 py-2 text-sm font-medium text-zeos-black hover:bg-zeos-grey-light md:self-center"
                >
                  Back to Screener
                </button>
              </div>
            ) : (
              <PropertyDropdown
                activeCompany={activeCompany}
                label="Incentives Available"
                isOpen={isDropdownOpen}
                setIsOpen={setIsDropdownOpen}
                selectedPropertyId={selectedPropertyId || undefined}
                onPropertySelect={handlePropertySelect}
                enableNavigateToProperty={true}
              />
            )}
          </div>

          <div className="col-span-9">
            <div className="mb-3 rounded-md border border-zeos-grey bg-white px-4 py-3">
              <div className="text-sm font-semibold text-zeos-black">
                {eligibilityLabel}
              </div>
              <div className="mt-1 text-sm text-slate-700">{eligibilityHint}</div>
            </div>
            {hasPendingAiAnalysis ? (
              <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                AI analysis is being prepared — refresh this page in a moment.
              </div>
            ) : null}
            <IncentivesTable
              incentives={displayIncentives}
              loading={displayLoading}
              error={displayError}
              propertyId={isScreeningDerived ? null : selectedPropertyId}
              screeningRunGuid={isScreeningDerived ? screeningRunGuid : null}
              isScreeningDerived={isScreeningDerived}
            />
          </div>

          <div className="col-span-3">
            <IncentivesResources
              selectedPropertyId={isScreeningDerived ? null : selectedPropertyId}
              incentives={displayIncentives}
              isSuperAdmin={isSuperAdmin}
              onRefresh={refresh}
            />
          </div>
        </div>
      </main>
      <DashboardFooter />
    </div>
  );
};
